import { useContext, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Layout, Input, Popover } from "antd";
import {
  SearchOutlined,
  QuestionCircleOutlined,
  ShopOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "./Sidebar";
import TOpsLogo from "../../assets/img/brand/t-ops-logo-dark.png";
import AppBreadcrumb from "../../constants/AppBreadcrumb";
import { renewSession } from "../../services/AuthService";
import { setIntervalID } from "../../store/actions/user";
import { BrandContext } from "../../context/BrandContext";
import { useOrgContext } from "../../hooks/useOrgContext";
import { useExitClientView } from "../../hooks/useExitClientView";
import {
  SearchProvider,
  SearchContext,
} from "../../context/SearchContext";

// A menu route owns a path if it's an exact match, or the path is a
// sub-route of it (e.g. menu route "/org-app-mapping" owns
// "/org-app-mapping/add" and "/org-app-mapping/edit/123") — create/edit
// screens aren't listed in menuConfig themselves, only their list page is.
function routeOwnsPath(route, pathname) {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function pathBelongsToModule(module, pathname) {
  return (
    (module.directPages || []).some((p) => routeOwnsPath(p.route, pathname)) ||
    (module.submodules || []).some((sub) =>
      (sub.pages || []).some((p) => routeOwnsPath(p.route, pathname)),
    )
  );
}

// Platform user inside a client (CLIENT_VIEW): always-visible chip naming the
// client being viewed, with a one-click way back to Manage Clients.
function ClientContextChip() {
  const mode = useSelector((state) => state.ui.mode);
  const userDetails = useSelector((state) => state.User.userDetails);
  const clientName = useSelector((state) => state.User.currentOrgName);
  const exitClientView = useExitClientView();
  if (userDetails?.orgType !== "PLATFORM" || mode !== "CLIENT_VIEW" || !clientName) {
    return null;
  }
  return (
    <div className="client-chip" title={`Viewing client: ${clientName}`}>
      <ShopOutlined className="client-chip__icon" />
      <span className="client-chip__label">Viewing client</span>
      <span className="client-chip__name">{clientName}</span>
      <button
        type="button"
        className="client-chip__exit"
        onClick={exitClientView}
        title="Back to Manage Clients"
        aria-label="Back to Manage Clients"
      >
        <CloseOutlined />
      </button>
    </div>
  );
}

// Portal name before the breadcrumb, for client users — platform users
// already see the T-Ops logo in the sidebar brand band
function PortalName() {
  const { isPlatformUser } = useOrgContext();
  if (isPlatformUser) return null;
  return (
    <div className="page-topbar-portal">
      <img src={TOpsLogo} alt="T-Ops" />
    </div>
  );
}

function TopBar() {
  const { inputValue, hideSearch, handleInputChange, handleSearch } =
    useContext(SearchContext);
  return (
    <div className="page-topbar">
      <div className="page-topbar-left">
        <ClientContextChip />
        <PortalName />
        <AppBreadcrumb />
      </div>
      <div className="page-topbar-right">
        {!hideSearch && (
          <Input
            placeholder="Search something here..."
            prefix={<SearchOutlined style={{ color: "rgba(0, 0, 0, 0.6)" }} />}
            className="page-topbar-search"
            value={inputValue}
            onChange={handleInputChange}
            onPressEnter={(e) => handleSearch(e.target.value)}
            allowClear
          />
        )}
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <div className="need-help-popover">
              Contact your system administrator for assistance.
            </div>
          }
        >
          <button type="button" className="need-help-btn">
            <QuestionCircleOutlined />
            <span className="need-help-label">Need help</span>
          </button>
        </Popover>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const location = useLocation();
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.User.userDetails);
  const intervalID = useSelector((state) => state.User.intervalID);
  const mode = useSelector((state) => state.ui.mode);
  const menuData = useSelector((state) => state.menu.menuData);
  // Platform user = only orgs with orgType "PLATFORM"
  const isPlatformUser = userDetails?.orgType === "PLATFORM";
  const { applyOrgTheme, resetToPlatformTheme } = useContext(BrandContext);
  const { contextOrgId } = useOrgContext();

  // Theme: a client org user gets their own org's theme. A platform user
  // ALWAYS keeps the platform's own theme — also while viewing a client
  // (CLIENT_VIEW) — so it's never mistaken for that client's own login; the
  // topbar client chip says which client is being viewed. Platform staff
  // carry their own orgId (e.g. an internal "Trade Hub" org — see
  // useOrgContext), so that org's theme mapping applies; the org-agnostic
  // platform theme is only the fallback when there's no orgId at all.
  useEffect(() => {
    const themeOrgId = isPlatformUser
      ? userDetails?.orgId
      : contextOrgId || userDetails?.orgId;
    if (themeOrgId) {
      applyOrgTheme(themeOrgId);
    } else if (isPlatformUser) {
      resetToPlatformTheme();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextOrgId, isPlatformUser, userDetails?.orgId]);

  // Session renewal
  useEffect(() => {
    if (!intervalID) {
      dispatch(
        setIntervalID(
          setInterval(() => {
            dispatch(renewSession());
          }, 780000),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set mode based on user type and current route
  useEffect(() => {
    if (isPlatformUser) {
      // Routes under /client/* are always CLIENT_VIEW
      if (location.pathname.startsWith("/client/")) {
        if (mode !== "CLIENT_VIEW") {
          dispatch({ type: "SET_MODE", payload: "CLIENT_VIEW" });
          dispatch({ type: "SET_SIDEBAR_OPEN_KEYS", payload: ["module-700"] });
        }
        return;
      }

      // If already in CLIENT_VIEW, check if this route belongs to a CLIENT_VIEW
      // module (e.g. /org-app-mapping from Subscription). If so, stay in
      // CLIENT_VIEW — don't flip back to PLATFORM just because the path
      // doesn't start with /client/.
      if (mode === "CLIENT_VIEW" && menuData) {
        const isClientViewRoute = (menuData.modules || [])
          .filter((m) => m.mode === "CLIENT_VIEW" && m.isActive)
          .some((m) => pathBelongsToModule(m, location.pathname));
        if (isClientViewRoute) return;
      }

      // Default for platform users: PLATFORM mode
      if (mode !== "PLATFORM") {
        dispatch({ type: "SET_MODE", payload: "PLATFORM" });
        dispatch({ type: "SET_SIDEBAR_OPEN_KEYS", payload: [] });
      }
      return;
    }
    // CLIENT org user: reset any cross-contamination from PLATFORM/CLIENT_VIEW mode
    if (mode === "PLATFORM" || mode === "CLIENT_VIEW") {
      dispatch({ type: "SET_MODE", payload: "NORMAL" });
      return;
    }

    // Reset stale SETTINGS mode — settings are now shown inline in the sidebar,
    // not as a separate mode. Any lingering SETTINGS mode state resets to NORMAL.
    if (mode === "SETTINGS") {
      dispatch({ type: "SET_MODE", payload: "NORMAL" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformUser, location.pathname, menuData]);

  return (
    <SearchProvider>
      <Layout style={{ minHeight: "100vh" }}>
        <Sidebar />
        <section className="home-section">
          <TopBar />
          <div className="page-content-canvas">
            <Outlet />
          </div>
        </section>
      </Layout>
    </SearchProvider>
  );
}
